// src/screens/HomeTabs.jsx
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  C, F, MAXW, card, tx, BEACHES, SEED_BLOGS,
  getRank, getNext, pct, sevColor
} from "../constants";
import { Icon, XPBar, Skeleton, Empty, ErrorState } from "../components/UI";
import { CREDIT, ORG } from "../eh/org";
import { PartnerStrip } from "../components/PartnerLockup";

// ─── Blog Article Full Reader ─────────────────────────────────────────────────
export function ArticleReader({ article, onClose }) {
  if (!article) return null;

  const renderContent = (text) => {
    return text.split("\n\n").map((para, i) => {
      if (para.startsWith("**") && para.endsWith("**")) {
        return (
          <h3 key={i} style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: "16px 0 8px", fontFamily: F }}>
            {para.replace(/\*\*/g, "")}
          </h3>
        );
      }
      const parts = para.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} style={{ fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: 14 }}>
          {parts.map((p, j) =>
            p.startsWith("**") && p.endsWith("**")
              ? <strong key={j} style={{ color: C.text, fontWeight: 700 }}>{p.replace(/\*\*/g, "")}</strong>
              : p
          )}
        </p>
      );
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 600, background: C.bg, overflowY: "auto", scrollbarWidth: "none", maxWidth: MAXW.content, left: "50%", transform: "translateX(-50%)", boxShadow: C.shadowLg }}>
      <div style={{ position: "relative", height: 240 }}>
        <img src={article.coverImage} alt={article.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, rgba(16,36,46,0.16) 0%, ${C.bg} 100%)` }} />
        <button onClick={onClose} style={{ position: "absolute", top: 16, left: 16, width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.86)", backdropFilter: "blur(8px)", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <Icon name="arrow_back" size={20} color={C.text} />
        </button>
      </div>

      <div style={{ padding: "20px 20px 80px" }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.3, marginBottom: 12, fontFamily: F }}>{article.title}</h1>
        <div>{renderContent(article.content || article.excerpt || "")}</div>
      </div>
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, delay = 0 }) {
  return (
    <div style={{ ...card("raised"), borderColor: `${color}2a`, animation: `fadeUp .5s ease ${delay}s both` }}>
      <Icon name={icon} size={22} color={color} />
      <div style={{ fontFamily: F, fontSize: 22, fontWeight: 800, color, margin: "6px 0 4px" }}>{value}</div>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px" }}>{label}</div>
    </div>
  );
}

function SectionTitle({ title, sub, action, onAction }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{sub}</div>}
      </div>
      {action && (
        <button onClick={onAction} style={{ background: "none", border: "none", fontSize: 12, color: C.primary, cursor: "pointer", fontFamily: F, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0, padding: 0 }}>
          {action}
        </button>
      )}
    </div>
  );
}

// ─── Main Home Dashboard ──────────────────────────────────────────────────────
export default function HomeTabs({ setTab, lang }) {
  const { profile } = useAuth();
  const [reports, setReports] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loadRpts, setLoadRpts] = useState(true);
  const [rptErr, setRptErr] = useState(null);
  const [loadBlogs, setLoadBlogs] = useState(true);
  const [blogErr, setBlogErr] = useState(null);
  const [readingArticle, setReading] = useState(null);

  const t = (k) => tx(lang, k);
  const rank = getRank(profile?.pts || 0);
  const nr = getNext(profile?.pts || 0);
  const prog = nr ? pct((profile?.pts || 0) - rank.min, rank.max - rank.min) : 100;
  const role = profile?.role || "reporter";
  const roleColor = role === "cleanup" ? C.green : role === "ngo" ? C.gold : C.primary;

  // A failed fetch used to substitute two invented reports with invented
  // reporter names. On a dashboard whose whole purpose is to show what is
  // actually happening on the coast, that is worse than showing nothing.
  const fetchRpts = useCallback(async () => {
    setLoadRpts(true);
    setRptErr(null);
    try {
      // Imported here rather than at module scope so the Firestore SDK lands in
      // its own chunk. The home screen renders its skeletons immediately and the
      // SDK arrives alongside the data it is being fetched for.
      const { collection, query, orderBy, limit, getDocs } = await import("firebase/firestore");
      const { db } = await import("../firebase");
      const q = query(collection(db, "reports"), orderBy("createdAt", "desc"), limit(5));
      const snap = await getDocs(q);
      setReports(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Latest reports failed to load:", err);
      setReports([]);
      setRptErr(
        navigator.onLine === false
          ? "You are offline, so the latest reports could not load."
          : "The latest reports could not be loaded."
      );
    }
    setLoadRpts(false);
  }, []);

  // SEED_BLOGS is editorial content that ships with the app, not fabricated
  // activity data, so falling back to it is honest — these articles are real.
  const fetchBlogs = useCallback(async () => {
    setLoadBlogs(true);
    setBlogErr(null);
    try {
      const { collection, query, orderBy, limit, getDocs } = await import("firebase/firestore");
      const { db } = await import("../firebase");
      const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"), limit(4));
      const snap = await getDocs(q);
      setBlogs(snap.empty ? SEED_BLOGS.slice(0, 4) : snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Blog list failed to load, showing the bundled articles:", err);
      setBlogs(SEED_BLOGS.slice(0, 4));
      setBlogErr(true);
    }
    setLoadBlogs(false);
  }, []);

  useEffect(() => { fetchRpts(); }, [fetchRpts]);
  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const timeAgo = (ts) => {
    if (!ts?.seconds) return "Recent";
    const d = (Date.now() / 1000 - ts.seconds);
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  };

  const featuredBlog = blogs[0];

  const reporterStats = [
    { icon: "assignment", label: t("reports"), value: profile?.reports || 0, color: C.primary },
    { icon: "military_tech", label: "Badges", value: (profile?.badgesEarned || []).length, color: C.gold },
    { icon: "local_fire_department", label: t("streak"), value: `${profile?.streak || 0}d`, color: C.coral },
    { icon: "stars", label: "Points", value: profile?.pts || 0, color: C.green },
  ];

  const cleanupStats = [
    { icon: "cleaning_services", label: "Cleanups", value: profile?.cleanups || 0, color: C.green },
    { icon: "task_alt", label: "Missions", value: profile?.claimedChallenges?.length || 0, color: C.primary },
    { icon: "local_fire_department", label: t("streak"), value: `${profile?.streak || 0}d`, color: C.coral },
    { icon: "stars", label: "XP", value: profile?.pts || 0, color: C.gold },
  ];

  // Every tile here reads a real value. "Volunteers: 42" used to be hardcoded;
  // there is no volunteer roster in this data model, so the tile is gone rather
  // than showing a number a coordinator might quote to a funder.
  const ngoStats = [
    { icon: "map", label: "Beaches tracked", value: BEACHES.length, color: C.primary },
    { icon: "report", label: "Recent reports", value: reports.length, color: C.coral },
    { icon: "insights", label: "Cleanups led", value: profile?.cleanups || 0, color: C.green },
    { icon: "stars", label: "Points", value: (profile?.pts || 0).toLocaleString(), color: C.gold },
  ];

  return (
    <>
      {readingArticle && <ArticleReader article={readingArticle} onClose={() => setReading(null)} />}

      <div style={{ padding: "0 0 16px", position: "relative", zIndex: 1 }}>
        {/* Hero */}
        <div style={{ margin: "12px 16px 0", ...card("keyed", roleColor), borderRadius: 20, padding: 18, animation: "fadeUp .4s ease both" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `${rank.color}18`, border: `1.5px solid ${rank.color}35`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={rank.icon} size={26} color={rank.color} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: F, lineHeight: 1.1 }}>
                {profile?.name?.split(" ")[0]}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                {role === "cleanup" ? "Cleanup Crew" : role === "ngo" ? "NGO Coordinator" : "Community Reporter"} · {rank.title}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: rank.color, fontFamily: F }}>{(profile?.pts || 0).toLocaleString()}</div>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: "1px" }}>PTS</div>
            </div>
          </div>

          <XPBar pts={profile?.pts || 0} />

          <button
            onClick={() => setTab(role === "ngo" ? "map" : role === "cleanup" ? "missions" : "report")}
            style={{ width: "100%", marginTop: 14, padding: "14px", background: roleColor, border: "none", borderRadius: 14, fontFamily: F, fontSize: 14, fontWeight: 700, color: C.bg, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" }}
          >
            <Icon
              name={role === "cleanup" ? "cleaning_services" : role === "ngo" ? "monitoring" : "add_a_photo"}
              size={18}
              color={C.bg}
            />
            {role === "cleanup"
              ? "Open Missions"
              : role === "ngo"
              ? "Open Beach Monitor"
              : t("reportNow")}
          </button>
        </div>

        {/* ROLE-SPECIFIC BODY */}
        {role === "reporter" && (
          <>
            <div style={{ padding: "14px 16px 0" }}>
              <SectionTitle title="Your reporting" sub="What you have submitted so far" />
              <div className="cc-tiles">
                {reporterStats.map((s, i) => <StatCard key={s.label} {...s} delay={i * 0.06} />)}
              </div>
            </div>

            <div style={{ margin: "14px 16px 0", ...card("keyed", C.primary), display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ width: 42, height: 42, borderRadius: "50%", background: `${C.primary}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="photo_camera" size={22} color={C.primary} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F }}>A photo is what gets a site acted on</div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginTop: 3 }}>
                  A coordinator can judge the health risk from a photo and send the right response. Reports without one usually wait for someone to go and look.
                </div>
              </div>
            </div>
          </>
        )}

        {role === "cleanup" && (
          <>
            <div style={{ padding: "14px 16px 0" }}>
              <SectionTitle title="Your cleanups" sub="Missions claimed and completed" />
              <div className="cc-tiles">
                {cleanupStats.map((s, i) => <StatCard key={s.label} {...s} delay={i * 0.06} />)}
              </div>
            </div>

            <div style={{ margin: "14px 16px 0", ...card("keyed", C.green) }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", background: `${C.green}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="task_alt" size={22} color={C.green} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F }}>Before and after, every time</div>
                  <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginTop: 3 }}>
                    Photograph the site when you claim it and again when you finish. The pair is what proves the cleanup happened.
                  </div>
                </div>
              </div>
              <button
                onClick={() => setTab("missions")}
                style={{ marginTop: 12, width: "100%", padding: "12px", background: `${C.green}18`, border: `1px solid ${C.green}44`, borderRadius: 12, color: C.green, fontWeight: 700, fontFamily: F, cursor: "pointer" }}
              >
                Open missions
              </button>
            </div>
          </>
        )}

        {role === "ngo" && (
          <>
            <div style={{ padding: "14px 16px 0" }}>
              <SectionTitle title="Coordination" sub="What is coming in from the coast" />
              <div className="cc-tiles">
                {ngoStats.map((s, i) => <StatCard key={s.label} {...s} delay={i * 0.06} />)}
              </div>
            </div>

            <div style={{ margin: "14px 16px 0", ...card("keyed", C.gold) }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", background: `${C.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="monitoring" size={22} color={C.gold} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F }}>Where to send crews next</div>
                  <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginTop: 3 }}>
                    The beach view ranks sites by how many reports came in and how severe they were.
                  </div>
                </div>
              </div>
              <button
                onClick={() => setTab("map")}
                style={{ marginTop: 12, width: "100%", padding: "12px", background: `${C.gold}18`, border: `1px solid ${C.gold}44`, borderRadius: 12, color: C.gold, fontWeight: 700, fontFamily: F, cursor: "pointer" }}
              >
                Open beach view
              </button>
            </div>
          </>
        )}

        {/* The feed and the reading list. Stacked on a phone, side by side from
            a desktop up: on a wide canvas a single report row stretched across
            the full column is mostly empty space, and putting the two sections
            in one band uses that width instead of padding it. The wrapper is
            the only change — both sections keep their own markup and their
            phone order. */}
        <div className="cc-duo">
          {/* Shared latest reports */}
          <div style={{ padding: "16px 16px 0" }}>
            <SectionTitle
              title={t("latestReports")}
              action="Map"
              onAction={() => setTab("map")}
            />
            {loadRpts ? (
              <Skeleton rows={3} height={68} />
            ) : rptErr ? (
              <ErrorState
                title="Could not load the latest reports"
                detail={`${rptErr} Anything you submit is still sent normally.`}
                onRetry={fetchRpts}
              />
            ) : reports.length === 0 ? (
              <Empty
                icon="pin_drop"
                title="No reports yet"
                sub="Nothing has been reported on the coast so far. The first one can be yours."
                actionLabel={role === "reporter" ? "Report a site" : undefined}
                onAction={role === "reporter" ? () => setTab("report") : undefined}
              />
            ) : reports.map(r => (
              <div key={r.id} style={{ ...card("flat"), marginBottom: 8, display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: `${sevColor(r.severity)}1a`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                  {r.photoURL
                    ? <img src={r.photoURL} alt={`Waste reported at ${r.beach || "an unnamed beach"}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <Icon name="location_on" size={20} color={sevColor(r.severity)} />
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F }}>{r.beach}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{r.waste} · {r.userName}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {/* Severity is spelled out, not left to the colour of a stripe. */}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${sevColor(r.severity)}1e`, border: `1px solid ${sevColor(r.severity)}44`, borderRadius: 99, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: sevColor(r.severity) }}>
                    <Icon name={r.severity === "High" ? "priority_high" : r.severity === "Medium" ? "remove" : "check"} size={11} color={sevColor(r.severity)} />
                    {r.severity || "Unrated"}
                  </span>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{timeAgo(r.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Shared learn section */}
          <div style={{ padding: "16px 16px 0" }}>
            <SectionTitle title={t("learnTitle")} sub={t("learnSub")} action="All articles" onAction={() => setTab("learn")} />
            {loadBlogs ? (
              <Skeleton rows={1} height={210} />
            ) : !featuredBlog ? (
              <Empty icon="menu_book" title="No articles yet" sub="Guides on coastal waste and health will appear here." />
            ) : (
              <button
                onClick={() => setReading(featuredBlog)}
                style={{ display: "block", width: "100%", textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", marginBottom: 10, cursor: "pointer", padding: 0, fontFamily: F, animation: "fadeUp .5s ease both" }}
              >
                <img src={featuredBlog.coverImage} alt="" style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }} />
                <div style={{ padding: "12px 14px 14px" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F, lineHeight: 1.3 }}>{featuredBlog.title}</div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{featuredBlog.excerpt}</div>
                  {blogErr && (
                    <div style={{ fontSize: 10.5, color: C.gold, marginTop: 8, display: "flex", gap: 5, alignItems: "center" }}>
                      <Icon name="cloud_off" size={12} color={C.gold} />
                      Offline — showing the articles bundled with the app
                    </div>
                  )}
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Environmental health — the surveillance side of the platform */}
        <div style={{ margin: "16px 16px 0" }}>
          <button
            onClick={() => setTab("eh")}
            style={{
              width: "100%",
              ...card("keyed", C.primary),
              borderRadius: 18,
              textAlign: "left",
              cursor: "pointer",
              fontFamily: F,
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 13,
                background: `${C.primary}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon name="ecg_heart" size={22} color={C.primary} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Environmental Health</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>
                Standing water, blocked drains, flooding, waste burning. Survey households, log your field hours. Saves on the phone with no signal.
              </div>
            </div>
            <Icon name="chevron_right" size={20} color={C.primary} />
          </button>
        </div>

        <div style={{ margin: "10px 16px 0" }}>
          <button
            onClick={() => setTab("missions")}
            style={{
              width: "100%",
              padding: "14px",
              background: C.green,
              border: "none",
              borderRadius: 14,
              fontFamily: F,
              fontSize: 14,
              fontWeight: 700,
              color: C.bg,
              cursor: "pointer",
            }}
          >
            Join a cleanup mission
          </button>
        </div>

        {/* Shared beach cards */}
        <div style={{ padding: "16px 16px 0" }}>
          <SectionTitle title="Beaches" sub="Tap one to open it on the map" />
          <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
            {BEACHES.map(b => (
              <button
                key={b.id}
                onClick={() => setTab("map")}
                aria-label={`${b.name}, ${b.region} region — open on the map`}
                style={{ flexShrink: 0, width: 128, ...card("flat"), cursor: "pointer", textAlign: "left", fontFamily: F }}
              >
                <Icon name="location_on" size={16} color={C.primary} />
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: F, lineHeight: 1.25, marginTop: 7 }}>{b.name}</div>
                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 3 }}>{b.region}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Deployment attribution — facts sourced in src/eh/org.js */}
        <div
          style={{
            margin: "26px 16px 0",
            paddingTop: 16,
            borderTop: `1px solid ${C.border}`,
            textAlign: "center",
          }}
        >
          {/* The home screen sits on C.bg, so the marks get plates — their own
              white background would otherwise read as a pale rectangle. */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <PartnerStrip lang={lang} base={40} tiles />
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: C.text, marginBottom: 4 }}>{ORG.acronym}</div>
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.55 }}>{ORG.name}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>{CREDIT}</div>
        </div>
      </div>
    </>
  );
}